import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNotification } from '../../../contexts/NotificationContext';
import McpSettings from './McpSettings';
import { api } from '../../../services/api';

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

const IntegrationCard = styled.div`
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
`;

const IntegrationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const IntegrationInfo = styled.div`
  flex: 1;
`;

const IntegrationTitle = styled.h4`
  color: #333;
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
`;

const IntegrationDescription = styled.p`
  color: #666;
  margin: 0;
  font-size: 0.9rem;
`;

const StatusBadge = styled.span<{ $status: 'connected' | 'disconnected' | 'error' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${({ $status }) => {
    switch ($status) {
      case 'connected': return '#d4edda';
      case 'disconnected': return '#f8d7da';
      case 'error': return '#fff3cd';
      default: return '#e2e3e5';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'connected': return '#155724';
      case 'disconnected': return '#721c24';
      case 'error': return '#856404';
      default: return '#6c757d';
    }
  }};
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-right: 0.5rem;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfigButton = styled(ActionButton)`
  background: #6c757d;
`;

const DisconnectButton = styled(ActionButton)`
  background: white;
  color: #b42318;
  border: 1px solid #f0b4ae;
`;

const ConnectionDetails = styled.div`
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 6px;
  background: #f8f9fa;
  color: #444;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const StatCard = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
`;

const LastSync = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.5rem;
`;

const AssetList = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const AssetRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid #dfe3e6;
  border-radius: 6px;
  background: #fff;

  @media (max-width: 600px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const AssetDetails = styled.div`
  min-width: 0;
`;

const AssetName = styled.div`
  color: #333;
  font-weight: 600;
`;

const AssetMeta = styled.div`
  color: #666;
  font-size: 0.78rem;
  margin-top: 0.2rem;
  overflow-wrap: anywhere;
`;

const RemoveAssetButton = styled.button`
  flex: 0 0 auto;
  border: 1px solid #dc3545;
  color: #dc3545;
  background: white;
  border-radius: 4px;
  padding: 0.4rem 0.7rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface MetaAssetItem {
  id: string;
  asset_type: string;
  external_id: string;
  name?: string | null;
  username?: string | null;
  selected: boolean;
  active: boolean;
}

interface IntegrationStatus {
  canal_pro: {
    connected: boolean;
    last_sync: string | null;
    total_imported: number;
    errors: number;
    config_exists: boolean;
  };
  meta: {
    configured: boolean;
    connections: Array<{
      id: string;
      display_name?: string;
      status: string;
      last_synced_at?: string | null;
    }>;
    assets: Record<string, number>;
    asset_items: MetaAssetItem[];
  };
  higgsfield: {
    configured: boolean;
    connected: boolean;
    status: string;
    email?: string | null;
    display_name?: string | null;
    token_expires_at?: string | null;
    last_checked_at?: string | null;
    last_error?: string | null;
  };
}

const IntegrationsSettings: React.FC = () => {
  const [integrationStatus, setIntegrationStatus] = useState<IntegrationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    loadIntegrationStatus();
    const params = new URLSearchParams(window.location.search);
    const result = params.get('higgsfield');
    if (result === 'connected') {
      showNotification('Conta Higgsfield conectada com sucesso', 'success');
    } else if (result === 'error') {
      showNotification(params.get('detail') || 'Não foi possível conectar ao Higgsfield', 'error');
    }
    if (result) {
      params.delete('higgsfield');
      params.delete('detail');
      params.delete('id');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    }
    // A notificação é estável no provider e este efeito deve consumir o callback uma única vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadIntegrationStatus = async () => {
    const [metaResult, higgsfieldResult] = await Promise.allSettled([
      api.get('/meta/status'),
      api.get('/higgsfield/status'),
    ]);
    try {
      setIntegrationStatus({
        canal_pro: {
          connected: true,
          last_sync: '2025-06-26T20:30:00Z',
          total_imported: 1250,
          errors: 3,
          config_exists: true
        },
        meta: metaResult.status === 'fulfilled' ? metaResult.value.data : {
          configured: false,
          connections: [],
          assets: {},
          asset_items: [],
        },
        higgsfield: higgsfieldResult.status === 'fulfilled' ? higgsfieldResult.value.data : {
          configured: false,
          connected: false,
          status: 'error',
          last_error: 'Não foi possível consultar a integração agora.',
        },
      });
    } catch (error) {
      console.error('Erro ao carregar status das integrações:', error);
    }
  };

  const handleHiggsfieldConnect = async () => {
    try {
      setIsLoading(true);
      const response = await api.post('/higgsfield/oauth/start', {
        redirect_path: '/admin/settings?section=integrations',
      });
      window.location.assign(response.data.authorization_url);
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Não foi possível iniciar o login do Higgsfield', 'error');
      setIsLoading(false);
    }
  };

  const handleHiggsfieldTest = async () => {
    try {
      setIsLoading(true);
      await api.post('/higgsfield/test');
      await loadIntegrationStatus();
      showNotification('Conexão com o Higgsfield validada', 'success');
    } catch (error: any) {
      await loadIntegrationStatus();
      showNotification(error.response?.data?.detail || 'A sessão Higgsfield precisa ser reconectada', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleHiggsfieldDisconnect = async () => {
    if (!window.confirm('Desconectar a conta Higgsfield da iLaura? Nenhuma criação será apagada.')) return;
    try {
      setIsLoading(true);
      await api.delete('/higgsfield/connection');
      await loadIntegrationStatus();
      showNotification('Conta Higgsfield desconectada', 'success');
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Não foi possível desconectar o Higgsfield', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMetaConnect = async () => {
    try {
      setIsLoading(true);
      const response = await api.post('/meta/oauth/start', {
        redirect_path: '/admin/settings?section=integrations',
      });
      window.location.assign(response.data.authorization_url);
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Não foi possível iniciar o login da Meta', 'error');
      setIsLoading(false);
    }
  };

  const handleMetaSync = async () => {
    const connection = integrationStatus?.meta.connections.find(item => item.status === 'connected');
    if (!connection) return;
    try {
      setIsLoading(true);
      await api.post(`/meta/connections/${connection.id}/sync`);
      await loadIntegrationStatus();
      showNotification('Contas e ativos da Meta atualizados', 'success');
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Falha ao atualizar a Meta', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMetaAssetRemoval = async (asset: MetaAssetItem) => {
    const label = asset.name || asset.username || asset.external_id;
    if (!window.confirm(
      `Remover "${label}" da iLaura? O ativo não será apagado na Meta.`
    )) return;
    try {
      setIsLoading(true);
      await api.patch(`/meta/assets/${asset.id}`, { selected: false });
      await loadIntegrationStatus();
      showNotification('Ativo Meta removido da iLaura', 'success');
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Falha ao atualizar o ativo Meta', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const metaAssetTypeLabel = (assetType: string) => ({
    facebook_page: 'Página do Facebook',
    instagram_account: 'Conta do Instagram',
    ad_account: 'Conta de anúncios',
    business: 'Portfólio empresarial',
  }[assetType] || assetType);

  const handleCanalProConfig = () => {
    // Redirecionar para a página de configuração do CanalPro
    window.location.href = '/admin/integrations/canal-pro';
  };

  const handleTestConnection = async (integration: string) => {
    try {
      setIsLoading(true);
      // TODO: Implementar teste de conexão
      showNotification(`Testando conexão com ${integration}...`, 'success');
      
      // Simular teste
      await new Promise(resolve => setTimeout(resolve, 2000));
      showNotification(`Conexão com ${integration} testada com sucesso!`, 'success');
    } catch (error) {
      showNotification(`Erro ao testar conexão com ${integration}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const formatLastSync = (dateString: string | null) => {
    if (!dateString) return 'Nunca sincronizado';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Agora mesmo';
    if (diffMinutes < 60) return `${diffMinutes} minutos atrás`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} horas atrás`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} dias atrás`;
  };

  if (!integrationStatus) {
    return <div>Carregando integrações...</div>;
  }

  return (
    <>
      <McpSettings />
      <Section>
        <SectionTitle>🔗 Integrações Ativas</SectionTitle>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Gerencie as integrações com plataformas externas para importação automática de propriedades.
        </p>

        {/* CanalPro Integration */}
        <IntegrationCard>
          <IntegrationHeader>
            <IntegrationInfo>
              <IntegrationTitle>
                🏢 CanalPro (ZAP Group)
                <StatusBadge $status={integrationStatus.canal_pro.connected ? 'connected' : 'disconnected'}>
                  {integrationStatus.canal_pro.connected ? '🟢 Conectado' : '🔴 Desconectado'}
                </StatusBadge>
              </IntegrationTitle>
              <IntegrationDescription>
                Importação automática de propriedades da plataforma CanalPro do Grupo ZAP (ZAP Imóveis, Viva Real, etc.)
              </IntegrationDescription>
            </IntegrationInfo>
          </IntegrationHeader>

          <StatsGrid>
            <StatCard>
              <StatValue>{integrationStatus.canal_pro.total_imported}</StatValue>
              <StatLabel>Imóveis Importados</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{integrationStatus.canal_pro.errors}</StatValue>
              <StatLabel>Erros Recentes</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>24h</StatValue>
              <StatLabel>Intervalo Sync</StatLabel>
            </StatCard>
          </StatsGrid>

          <LastSync>
            🕐 Última sincronização: {formatLastSync(integrationStatus.canal_pro.last_sync)}
          </LastSync>

          <div style={{ marginTop: '1rem' }}>
            <ConfigButton onClick={handleCanalProConfig}>
              ⚙️ Configurar
            </ConfigButton>
            <ActionButton 
              onClick={() => handleTestConnection('CanalPro')}
              disabled={isLoading}
            >
              {isLoading ? '⏳ Testando...' : '🔍 Testar Conexão'}
            </ActionButton>
          </div>
        </IntegrationCard>

        <IntegrationCard>
          <IntegrationHeader>
            <IntegrationInfo>
              <IntegrationTitle>
                🎬 Higgsfield AI{' '}
                <StatusBadge $status={
                  integrationStatus.higgsfield.connected
                    ? 'connected'
                    : integrationStatus.higgsfield.status === 'error'
                      ? 'error'
                      : 'disconnected'
                }>
                  {integrationStatus.higgsfield.connected ? '🟢 Conectado' : '🔴 Não conectado'}
                </StatusBadge>
              </IntegrationTitle>
              <IntegrationDescription>
                Geração de clipes verticais a partir das fotos dos imóveis, usando os modelos e créditos da conta conectada.
              </IntegrationDescription>
            </IntegrationInfo>
          </IntegrationHeader>

          {integrationStatus.higgsfield.connected && (
            <ConnectionDetails>
              <strong>Conta:</strong>{' '}
              {integrationStatus.higgsfield.display_name || integrationStatus.higgsfield.email || 'Higgsfield'}
              {integrationStatus.higgsfield.display_name && integrationStatus.higgsfield.email
                ? ` · ${integrationStatus.higgsfield.email}`
                : ''}
              {integrationStatus.higgsfield.last_checked_at && (
                <LastSync>
                  Última validação: {formatLastSync(integrationStatus.higgsfield.last_checked_at)}
                </LastSync>
              )}
            </ConnectionDetails>
          )}

          {integrationStatus.higgsfield.last_error && (
            <p style={{ color: '#856404', marginTop: '1rem' }}>
              {integrationStatus.higgsfield.last_error}
            </p>
          )}

          {!integrationStatus.higgsfield.configured && (
            <p style={{ color: '#856404', marginTop: '1rem' }}>
              A chave de criptografia do Higgsfield ainda precisa ser configurada no backend.
            </p>
          )}

          <div style={{ marginTop: '1rem' }}>
            <ActionButton
              type="button"
              onClick={handleHiggsfieldConnect}
              disabled={isLoading || !integrationStatus.higgsfield.configured}
            >
              {integrationStatus.higgsfield.connected ? 'Reconectar conta' : 'Conectar com Higgsfield'}
            </ActionButton>
            {integrationStatus.higgsfield.connected && (
              <>
                <ConfigButton type="button" onClick={handleHiggsfieldTest} disabled={isLoading}>
                  Validar conexão
                </ConfigButton>
                <DisconnectButton type="button" onClick={handleHiggsfieldDisconnect} disabled={isLoading}>
                  Desconectar
                </DisconnectButton>
              </>
            )}
          </div>
        </IntegrationCard>

        <IntegrationCard>
          <IntegrationHeader>
            <IntegrationInfo>
              <IntegrationTitle>
                Meta — Instagram, Facebook e Ads{' '}
                <StatusBadge $status={integrationStatus.meta.connections.some(item => item.status === 'connected') ? 'connected' : 'disconnected'}>
                  {integrationStatus.meta.connections.some(item => item.status === 'connected') ? '🟢 Conectado' : '🔴 Não conectado'}
                </StatusBadge>
              </IntegrationTitle>
              <IntegrationDescription>
                Publicação orgânica com revisão e gestão direta de campanhas pela API oficial do Meta Ads.
              </IntegrationDescription>
            </IntegrationInfo>
          </IntegrationHeader>

          <StatsGrid>
            <StatCard>
              <StatValue>{integrationStatus.meta.assets.facebook_page || 0}</StatValue>
              <StatLabel>Páginas</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{integrationStatus.meta.assets.instagram_account || 0}</StatValue>
              <StatLabel>Contas Instagram</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{integrationStatus.meta.assets.ad_account || 0}</StatValue>
              <StatLabel>Contas de anúncios</StatLabel>
            </StatCard>
          </StatsGrid>

          {!!integrationStatus.meta.asset_items?.length && (
            <>
              <h5 style={{ margin: '1.25rem 0 0.5rem', color: '#333' }}>Ativos configurados</h5>
              <AssetList>
                {integrationStatus.meta.asset_items.map(asset => (
                  <AssetRow key={asset.id}>
                    <AssetDetails>
                      <AssetName>
                        {asset.name || asset.username || asset.external_id}
                      </AssetName>
                      <AssetMeta>
                        {metaAssetTypeLabel(asset.asset_type)}
                        {asset.username ? ` · @${asset.username}` : ''}
                        {` · ID ${asset.external_id}`}
                      </AssetMeta>
                    </AssetDetails>
                    <RemoveAssetButton
                      type="button"
                      disabled={isLoading}
                      onClick={() => handleMetaAssetRemoval(asset)}
                    >
                      Remover
                    </RemoveAssetButton>
                  </AssetRow>
                ))}
              </AssetList>
            </>
          )}

          {!integrationStatus.meta.configured && (
            <p style={{ color: '#856404', marginTop: '1rem' }}>
              O aplicativo Meta ainda precisa das credenciais e da chave de criptografia no backend.
            </p>
          )}

          <div style={{ marginTop: '1rem' }}>
            <ActionButton onClick={handleMetaConnect} disabled={isLoading || !integrationStatus.meta.configured}>
              {integrationStatus.meta.connections.some(item => item.status === 'connected') ? 'Reconectar com a Meta' : 'Conectar com a Meta'}
            </ActionButton>
            {integrationStatus.meta.connections.some(item => item.status === 'connected') && (
              <ConfigButton onClick={handleMetaSync} disabled={isLoading}>
                Atualizar contas
              </ConfigButton>
            )}
          </div>
        </IntegrationCard>

        {/* Placeholder para futuras integrações */}
        <IntegrationCard>
          <IntegrationHeader>
            <IntegrationInfo>
              <IntegrationTitle>
                🏘️ OLX Imóveis
                <StatusBadge $status="disconnected">🔴 Não Configurado</StatusBadge>
              </IntegrationTitle>
              <IntegrationDescription>
                Integração com OLX Imóveis para importação e exportação de anúncios (Em desenvolvimento)
              </IntegrationDescription>
            </IntegrationInfo>
          </IntegrationHeader>

          <div style={{ marginTop: '1rem' }}>
            <ConfigButton disabled>
              ⚙️ Em Breve
            </ConfigButton>
          </div>
        </IntegrationCard>

        <IntegrationCard>
          <IntegrationHeader>
            <IntegrationInfo>
              <IntegrationTitle>
                📱 QuintoAndar API
                <StatusBadge $status="disconnected">🔴 Não Configurado</StatusBadge>
              </IntegrationTitle>
              <IntegrationDescription>
                Integração com QuintoAndar para sincronização de imóveis para locação (Planejado)
              </IntegrationDescription>
            </IntegrationInfo>
          </IntegrationHeader>

          <div style={{ marginTop: '1rem' }}>
            <ConfigButton disabled>
              ⚙️ Em Breve
            </ConfigButton>
          </div>
        </IntegrationCard>
      </Section>

      <Section>
        <SectionTitle>📊 Logs de Integração</SectionTitle>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Visualize os logs mais recentes das integrações ativas.
        </p>
        
        <div style={{ 
          background: '#f8f9fa', 
          padding: '1rem', 
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          <div style={{ color: '#28a745' }}>✅ [2025-06-26 20:30:15] CanalPro: Importação concluída - 45 propriedades processadas</div>
          <div style={{ color: '#007bff' }}>ℹ️ [2025-06-26 20:30:10] CanalPro: Iniciando sincronização automática</div>
          <div style={{ color: '#ffc107' }}>⚠️ [2025-06-26 18:15:32] CanalPro: 3 imagens falharam no download - URLs expiradas</div>
          <div style={{ color: '#28a745' }}>✅ [2025-06-26 12:00:15] CanalPro: Sincronização automática executada com sucesso</div>
          <div style={{ color: '#007bff' }}>ℹ️ [2025-06-26 06:00:05] Sistema: Agendamento automático ativo</div>
        </div>
      </Section>
    </>
  );
};

export default IntegrationsSettings;
